import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/lurneax-logo.svg";

export default function MobileLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr("");
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: pw,
    });
    setBusy(false);
    if (error) {
      setErr("Email or password is incorrect.");
      return;
    }
    navigate("/app/courses", { replace: true });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#FBEAE0] px-5">
      {/* clay corner accents */}
      <span className="pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full bg-[#F44321]/25" />
      <span className="pointer-events-none absolute -right-20 -bottom-20 h-56 w-56 rounded-full bg-[#EF9F27]/25" />

      <div className="relative flex w-full max-w-sm flex-col items-center">
        {/* brand logo on a light rounded tile */}
        <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-white shadow-[0_14px_32px_-12px_rgba(42,24,16,0.18)]">
          <img src={logo} alt="Lurneax" className="h-16 w-16 object-contain" />
        </div>

        <div className="mt-6 w-full rounded-3xl border border-[#F0DDD2] bg-[#FFF6EC] p-6 shadow-[0_24px_60px_-30px_rgba(42,24,16,0.35)]">
          <h1 className="text-center text-xl font-extrabold text-[#2A1810]">Welcome back</h1>
          <p className="mt-1 text-center text-xs font-semibold text-[#8A6050]">
            Sign in to Lurneax LMS
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-[11px] font-extrabold tracking-wider text-[#5A4035]">EMAIL</label>
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-[#F0DDD2] bg-[#F4E5DA] px-3 py-2.5 text-sm text-[#2A1810] outline-none focus:border-[#F44321]"
              />
            </div>

            <div>
              <label className="text-[11px] font-extrabold tracking-wider text-[#5A4035]">PASSWORD</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-[#F0DDD2] bg-[#F4E5DA] px-3">
                <input
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full bg-transparent py-2.5 text-sm text-[#2A1810] outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="text-[#8A6050]"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <a
                href="mailto:support@lurneax.com?subject=Password%20reset"
                className="text-xs font-bold text-[#F44321]"
              >
                Forgot password?
              </a>
            </div>

            {err && (
              <p className="rounded-lg bg-[#F44321]/10 px-3 py-2 text-xs font-semibold text-[#B8431D]">
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email || !pw}
              className="w-full rounded-xl bg-[#F44321] py-3 text-sm font-extrabold text-white shadow-[0_12px_24px_-12px_rgba(244,67,33,0.7)] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>

            <p className="pt-1 text-center text-[11px] font-semibold text-[#8A6050]">
              No account yet? Your centre admin sets these up.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
